import { useCallback, useEffect, useState } from 'react';
import type { VerseEnv } from '@rb/shared/admin-verses';
import { EMPTY_MAINTENANCE_STATE, type MaintenanceState } from '@rb/shared/maintenance.types';
import type { R2Flavor } from '@rb/shared/r2-flavor';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { AdminRPCClient } from '@/client/rpc-client';
import {
  fetchMaintenanceFlavored,
  publishMaintenanceFlavored,
} from '@/master-data/lib/maintenance';
import { useVerseEnvChannel } from '@/verse/use-verse-env-channel';

const NO_KEY = '__none__';

const CHANNELS = ['dev', 'staging', 'prod'] as const;
const FLAVORS: R2Flavor[] = ['web2', 'web3'];

const MESSAGE_KEY_OPTIONS: { value: string; label: string }[] = [
  { value: NO_KEY, label: '(기본 fallback / customMessage 사용)' },
  { value: 'MAINTENANCE_MSG_GENERAL', label: '일반 점검' },
  { value: 'MAINTENANCE_MSG_DEPLOY', label: '배포 작업' },
  { value: 'MAINTENANCE_MSG_EMERGENCY', label: '긴급 점검' },
  { value: 'MAINTENANCE_MSG_ROLLBACK', label: '롤백' },
];

function shortAccount(addr?: string): string {
  if (!addr) return '';
  return addr.length > 10 ? `${addr.slice(0, 6)}…${addr.slice(-4)}` : addr;
}

function emptyForms(): { web2: MaintenanceState; web3: MaintenanceState } {
  return {
    web2: { ...EMPTY_MAINTENANCE_STATE },
    web3: { ...EMPTY_MAINTENANCE_STATE },
  };
}

export default function MaintenancePage() {
  const { channel, setChannel, verseEnv } = useVerseEnvChannel();
  const [flavor, setFlavor] = useState<R2Flavor>('web3');
  const [forms, setForms] = useState<{ web2: MaintenanceState; web3: MaintenanceState }>(
    emptyForms
  );
  // 서버 게이트 상태(현재 연결된 verse 한정) — 표시용
  const [serverState, setServerState] = useState<MaintenanceState>({ enabled: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // R2 로드 성공 여부 — false 면 발행/토글 차단 (로드 실패 시 empty forms 로 덮어쓰기 방지)
  const [loaded, setLoaded] = useState(false);

  // 활성 flavor 의 폼 편의 접근자
  const form = forms[flavor];

  const messageKey = form.messageKey ?? NO_KEY;
  const customMessage = form.customMessage ?? form.message ?? '';

  const setFormField = (patch: Partial<MaintenanceState>) =>
    setForms((p) => ({ ...p, [flavor]: { ...p[flavor], ...patch } }));

  // RPC 로 현재 연결 verse 의 서버 게이트 상태 조회 (표시용)
  const fetchServerState = useCallback(async () => {
    try {
      const state = await AdminRPCClient.call<MaintenanceState>('getMaintenanceState');
      setServerState(state);
    } catch {
      // 서버 연결 실패 시 무시 — R2 상태가 우선
    }
  }, []);

  // 채널이 바뀌면 R2 발행본 폼 로드
  useEffect(() => {
    setLoaded(false);
    void (async () => {
      setError(null);
      try {
        const flavored = await fetchMaintenanceFlavored(channel);
        setForms(flavored);
        setLoaded(true);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'R2 발행본 로드 실패');
      }
    })();
  }, [channel]);

  // 초기 서버 상태 로드
  useEffect(() => {
    void fetchServerState();
  }, [fetchServerState]);

  const handleToggle = async (enabled: boolean) => {
    // R2 로드가 완료되지 않은 상태에서 발행하면 empty forms 로 sibling flavor 가 덮어써진다
    if (!loaded) return;
    if (
      channel === 'prod' &&
      !window.confirm('prod 채널의 점검 설정을 변경합니다. 계속하시겠습니까?')
    ) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const next: MaintenanceState = {
        enabled,
        messageKey: messageKey === NO_KEY ? undefined : messageKey,
        customMessage: customMessage.trim() || undefined,
      };
      const nextForms = { ...forms, [flavor]: next };
      setForms(nextForms);

      // (a) R2 발행 — 항상 수행 (pod 다운 시에도 클라 표시)
      await publishMaintenanceFlavored(channel, nextForms);

      // (b) 서버 게이트+broadcast — best-effort (현재 연결된 verse 한정, 실패해도 무시)
      try {
        await AdminRPCClient.call('setMaintenanceState', [next]);
        await fetchServerState();
      } catch (e) {
        console.warn('[maintenance] setMaintenanceState RPC 실패 (R2 발행은 완료)', e);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '점검 상태 변경 실패');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="flex flex-col gap-4">
      <header>
        <h2 className="text-2xl font-semibold tracking-tight">Maintenance Mode</h2>
        <p className="text-sm text-muted-foreground">
          R2 발행으로 pod 다운 시에도 표시, RPC 는 서버 게이트용 (현재 연결된 verse 한정). 점검 ON
          시 비-admin 유저의 모든 RPC 는 즉시 차단됩니다.
        </p>
      </header>

      {/* 채널 선택 */}
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="maintenance-channel">채널</Label>
        <Select value={channel} onValueChange={(v) => setChannel(v as VerseEnv)}>
          <SelectTrigger id="maintenance-channel" className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CHANNELS.map((c) => (
              <SelectItem key={c} value={c}>
                {c}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {verseEnv && (
          <span className="text-xs text-muted-foreground">
            활성 verse 환경({verseEnv})에 자동 동기화됩니다 · 수동 변경 가능
          </span>
        )}
      </div>

      {/* flavor 토글 (web2 | web3) */}
      <div className="flex items-center gap-2">
        <Label>모드</Label>
        <fieldset className="flex rounded-md border m-0 p-0 border-0">
          {FLAVORS.map((f) => (
            <button
              key={f}
              type="button"
              aria-pressed={flavor === f}
              className={`px-4 py-1.5 text-sm font-medium transition-colors first:rounded-l-md last:rounded-r-md ${
                flavor === f
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-background hover:bg-muted text-muted-foreground'
              }`}
              onClick={() => setFlavor(f)}
            >
              {f}
            </button>
          ))}
        </fieldset>
        <span className="text-xs text-muted-foreground">
          발행 시 web2/web3 양쪽 동시 저장됩니다
        </span>
      </div>

      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="text-base">Server maintenance — {flavor}</CardTitle>
            <CardDescription>
              R2 기준: {forms[flavor].enabled ? '점검 중' : '정상 운영'}
              {' · '}
              서버 게이트(현재 verse): {serverState.enabled ? 'ON' : 'OFF'}
            </CardDescription>
          </div>
          {forms[flavor].enabled ? (
            <Badge variant="destructive">ON</Badge>
          ) : (
            <Badge className="bg-green-600 text-white">OFF</Badge>
          )}
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="maintenance-key">Message preset (i18n key)</Label>
            <Select
              value={messageKey}
              onValueChange={(v) => setFormField({ messageKey: v === NO_KEY ? undefined : v })}
            >
              <SelectTrigger id="maintenance-key">
                <SelectValue placeholder="선택" />
              </SelectTrigger>
              <SelectContent>
                {MESSAGE_KEY_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              dropdown 선택 시 클라가 `t(key)` 로 다국어 표시. 빈 값(기본) + custom 비우면 fallback
              `MAINTENANCE_MESSAGE` 사용.
            </p>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="maintenance-custom">Custom message (긴급용 자유 텍스트)</Label>
            <Textarea
              id="maintenance-custom"
              value={customMessage}
              onChange={(e) =>
                setFormField({ customMessage: e.target.value || undefined, message: undefined })
              }
              placeholder="긴급용 자유 텍스트 (locale 분기 없이 그대로 노출). 사전 등록된 key 가 없을 때 사용."
              rows={2}
            />
          </div>
          <div className="flex gap-3">
            <Button
              type="button"
              variant="destructive"
              onClick={() => handleToggle(true)}
              disabled={loading || !loaded}
              className="flex-1"
            >
              {forms[flavor].enabled ? 'Update message' : 'Enable maintenance'}
            </Button>
            <Button
              type="button"
              onClick={() => handleToggle(false)}
              disabled={loading || !loaded || !forms[flavor].enabled}
              className="flex-1 bg-green-600 hover:bg-green-500"
            >
              Disable maintenance
            </Button>
          </div>
          {forms[flavor].updatedAt && (
            <p className="text-xs text-muted-foreground">
              Last updated: {new Date(forms[flavor].updatedAt).toLocaleString()}
              {forms[flavor].updatedBy && (
                <>
                  {' by '}
                  <span className="font-mono">{shortAccount(forms[flavor].updatedBy)}</span>
                </>
              )}
            </p>
          )}
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>
    </section>
  );
}
